// src/components/Hero.js
import React from 'react';
import { motion } from 'framer-motion';
import { Zap, MapPin } from 'lucide-react';
import heroBg from '../assets/hero-bg.jpg';
import { useTranslation } from 'react-i18next';

const Hero = ({ setContactMessage, scrollToContact }) => {
  const { t } = useTranslation();

  const handleVerifyCoverage = () => {
    if (!navigator.geolocation) {
      setContactMessage(t('hero_coverage_error'));
      scrollToContact();
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;

        const message = `${t('hero_coverage_message')}
Lat: ${latitude.toFixed(6)}, Lng: ${longitude.toFixed(6)}`;

        setContactMessage(message);
        scrollToContact();
      },
      () => {
        setContactMessage(t('hero_coverage_error'));
        scrollToContact();
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
      }
    );
  };

  return (
    <section id="home" className="relative min-h-screen overflow-hidden">
      {/* Imagen de fondo */}
      <div
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: `url(${heroBg})` }}
      />

      {/* Overlay principal */}
      <div className="absolute inset-0 bg-slate-950/40" />

      {/* Fade inferior */}
      <div className="absolute bottom-0 left-0 right-0 h-64 bg-gradient-to-b from-transparent to-slate-950 pointer-events-none" />

      {/* Partículas */}
      <div className="absolute inset-0 pointer-events-none">
        {[...Array(10)].map((_, i) => (
          <motion.div
            key={i}
            initial={{ y: '110vh', x: `${Math.random() * 100}vw`, opacity: 0 }}
            animate={{ y: '-110vh', opacity: [0, 0.8, 0] }}
            transition={{
              duration: 15 + Math.random() * 10,
              repeat: Infinity,
              delay: i * 1.5,
              ease: 'linear',
            }}
            className="absolute w-3 h-3 md:w-4 md:h-4 bg-orange-500 rounded-full shadow-2xl shadow-orange-500/60 blur-md"
          />
        ))}
      </div>

      {/* Contenido */}
      <div className="relative z-10 min-h-screen flex items-center">
        <div className="container mx-auto px-6 text-center pt-24 md:pt-28">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1 }}
          >
            {/* TÍTULO */}
            <h1 className="text-6xl md:text-8xl font-light tracking-tight text-white mb-6">
              {t('hero_title_main')}{' '}
              <span className="font-bold text-orange-500">
                {t('hero_title_highlight')}
              </span>
              <br />
              <span className="block text-5xl md:text-7xl mt-4 font-light">
                {t('hero_subtitle')}
              </span>
            </h1>

            {/* DESCRIPCIÓN */}
            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 0.3 }}
              className="text-xl md:text-3xl text-slate-200 font-light max-w-4xl mx-auto mb-12 leading-relaxed"
            >
              {t('hero_desc_line1_prefix')}{' '}
              <span className="text-orange-400 font-semibold">
                100 Mbps
              </span>
              {t('hero_desc_line1_suffix')}
              <br />
              {t('hero_desc_line2')}
            </motion.p>

            {/* CTAs */}
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 0.6 }}
              className="flex flex-col sm:flex-row gap-6 justify-center items-center"
            >
              <a
                href="#planes"
                className="group inline-flex items-center gap-4 px-10 py-5 bg-gradient-to-r from-orange-500 to-orange-600 text-white font-semibold text-lg rounded-full shadow-2xl shadow-orange-500/40 hover:shadow-orange-500/60 hover:scale-105 transition-all duration-300"
              >
                <Zap className="w-6 h-6 group-hover:animate-pulse" />
                {t('hero_cta_plans')}
              </a>

              <button
                onClick={handleVerifyCoverage}
                className="inline-flex items-center gap-4 px-10 py-5 bg-white/10 backdrop-blur-md border-2 border-orange-500/50 text-white font-semibold text-lg rounded-full hover:bg-white/20 hover:border-orange-500 hover:scale-105 transition-all duration-300"
              >
                <MapPin className="w-6 h-6" />
                {t('hero_cta_coverage')}
              </button>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
